import { db } from '../firebase'; // Adjust this import path if needed
import { collection, query, where, getDocs, updateDoc, addDoc, serverTimestamp, doc } from 'firebase/firestore';

// Hardcoded metric definitions
const metricDefinitions = {
  company: [
    { metricKey: 'mrr', metricName: 'Monthly Recurring Revenue', unit: 'usd' },
    { metricKey: 'activeClients', metricName: 'Total Active Clients', unit: 'count' },
    { metricKey: 'retention', metricName: 'Client Retention Rate', unit: 'percent' },
  ],
  leads: [
    { metricKey: 'newLeads', metricName: 'New Leads', unit: 'count' },
    { metricKey: 'qualifiedLeadPercent', metricName: 'Qualified Lead %', unit: 'percent' },
    { metricKey: 'costPerLead', metricName: 'Cost per Lead', unit: 'usd' },
  ],
  sales: [
    { metricKey: 'newClientsSigned', metricName: 'New Clients Signed', unit: 'count' },
    { metricKey: 'conversionRate', metricName: 'Conversion Rate', unit: 'percent' },
    { metricKey: 'avgPackageSize', metricName: 'Average Package Size', unit: 'usd' },
  ],
};

const goalsCollection = collection(db, 'monthlyGoals');

/**
 * Fetches or creates goals for a specific month and year.
 * @param {number} year - The year (e.g., 2025)
 * @param {number} month - The month (1-12)
 * @returns {object} - An object with goals organized by category.
 */
export const getGoalsForMonth = async (year, month) => {
  const allGoals = { company: [], leads: [], sales: [] };

  for (const category in metricDefinitions) {
    for (const metric of metricDefinitions[category]) {
      const q = query(
        goalsCollection,
        where('metricKey', '==', metric.metricKey),
        where('year', '==', year),
        where('month', '==', month)
      );

      const querySnapshot = await getDocs(q);
      let goalDoc;

      if (querySnapshot.empty) {
        // Create a new goal document if it doesn't exist
        const newGoalData = {
          ...metric,
          category,
          year,
          month,
          target: 0,
          actual: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const docRef = await addDoc(goalsCollection, newGoalData);
        goalDoc = { id: docRef.id, ...newGoalData };
      } else {
        const doc = querySnapshot.docs[0];
        goalDoc = { id: doc.id, ...doc.data() };
      }
      
      allGoals[category].push(goalDoc);
    }
  }

  return allGoals;
};

/**
 * Updates the target for a specific goal document.
 * @param {string} docId - The ID of the document to update.
 * @param {number} newTarget - The new target value.
 */
export const updateGoalTarget = async (docId, newTarget) => {
  const docRef = doc(db, 'monthlyGoals', docId);
  await updateDoc(docRef, {
    target: newTarget,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Updates the actual value for a specific goal document.
 * @param {string} docId - The ID of the document to update.
 * @param {number} newActual - The new actual value.
 */
export const updateGoalActual = async (docId, newActual) => {
  const docRef = doc(db, 'monthlyGoals', docId);
  await updateDoc(docRef, {
    actual: newActual,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Updates both target and actual values for a specific goal document.
 * @param {string} docId - The ID of the document to update.
 * @param {number} newTarget - The new target value.
 * @param {number} newActual - The new actual value.
 */
export const updateGoalValues = async (docId, newTarget, newActual) => {
  const docRef = doc(db, 'monthlyGoals', docId);
  await updateDoc(docRef, {
    target: newTarget,
    actual: newActual,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Saves monthly targets for a specific goal (metricKey) for a given year.
 * Creates or updates goal documents for all 12 months.
 * @param {string} metricKey - The metric key (e.g., 'activeClients', 'mrr')
 * @param {number} year - The year (e.g., 2025)
 * @param {object} monthlyTargets - Object with month names as keys and target values as values
 *                                  e.g., { 'January': 100, 'February': 120, ... }
 */
export const saveMonthlyTargetsForYear = async (metricKey, year, monthlyTargets) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  // Find the category and metric info
  let category = null;
  let metricInfo = null;
  for (const cat in metricDefinitions) {
    const metric = metricDefinitions[cat].find(m => m.metricKey === metricKey);
    if (metric) {
      category = cat;
      metricInfo = metric;
      break;
    }
  }

  if (!category || !metricInfo) {
    throw new Error(`Metric key ${metricKey} not found`);
  }

  // Update or create goal documents for each month
  const updatePromises = monthNames.map(async (monthName, index) => {
    const monthNumber = monthNumbers[index];
    const targetValue = monthlyTargets[monthName] !== undefined && monthlyTargets[monthName] !== '' 
      ? parseFloat(monthlyTargets[monthName]) || 0 
      : 0;

    // Check if goal document exists for this month/year
    const q = query(
      goalsCollection,
      where('metricKey', '==', metricKey),
      where('year', '==', year),
      where('month', '==', monthNumber)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Create new goal document
      const newGoalData = {
        ...metricInfo,
        category,
        year,
        month: monthNumber,
        target: targetValue,
        actual: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await addDoc(goalsCollection, newGoalData);
    } else {
      // Update existing goal document
      const docRef = doc(db, 'monthlyGoals', querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        target: targetValue,
        updatedAt: serverTimestamp(),
      });
    }
  });

  await Promise.all(updatePromises);
};

/**
 * Gets monthly targets for a specific goal (metricKey) for a given year.
 * @param {string} metricKey - The metric key (e.g., 'activeClients', 'mrr')
 * @param {number} year - The year (e.g., 2025)
 * @returns {object} - Object with month names as keys and target values as values
 */
export const getMonthlyTargetsForYear = async (metricKey, year) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const monthlyTargets = {};

  const fetchPromises = monthNumbers.map(async (monthNumber, index) => {
    const monthName = monthNames[index];
    const q = query(
      goalsCollection,
      where('metricKey', '==', metricKey),
      where('year', '==', year),
      where('month', '==', monthNumber)
    );

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const goalData = querySnapshot.docs[0].data();
      monthlyTargets[monthName] = goalData.target || 0;
    } else {
      monthlyTargets[monthName] = 0;
    }
  });

  await Promise.all(fetchPromises);
  return monthlyTargets;
}; 